import { RLBaseService } from "./rl-base-service";
import * as Models from "../ts/rlAdmin.models";
import * as moment from "moment";
import { LocalCacheService } from "./local-cache-service";

export class EntityService extends RLBaseService {

	static $inject = ["localCacheService", "$resource", "$q", "$log"];

	lovPromiseCache: { [key: string]: ng.IPromise<Models.ICharLovMetaData> };

	private fiveMins = moment.duration(5, "minute");

	constructor(private localCacheService: LocalCacheService, private $resource: ng.resource.IResourceService, private $q: ng.IQService, private $log: ng.ILogService) {
		super($resource);

		this.lovPromiseCache = {};
	}

	getHeaderCharLovMetaData(charTypeID: number, tagLabel: string) {

		var key = `getHeaderCharLovMetaData_${charTypeID}_${tagLabel}`;
		var data = {
			tagLabel: tagLabel,
			charTypeID: charTypeID
		};
		return this.localCacheService.getCachedOrDoCachePromise(key, () => this.toRLService<Models.ICharLovMetaData>("/api/entity/getheadercharlovmetadata").get(data).$promise, moment.duration(5, 'minutes'));
	}

	getCharLovMetaData(charValueSourceID: string, charValueSetID: string = null) {

		var key = `getCharLovMetaData_${charValueSourceID}`;
		var data = {
			charValueSourceID: charValueSourceID,
			charValueSetID: charValueSetID
		};

		return this.localCacheService.getCachedOrDoCachePromise(key, () => this.toRLService<Models.ICharLovMetaData>("/api/entity/getcharlovmetadata").get(data).$promise, moment.duration(5, 'minutes'));
	}

	createEntityRelationships(entityID: string, childrenEntityIDs: string[]) {
		var query = {
			entityID: entityID,
			children: childrenEntityIDs
		}

		return this.toRLService<Models.ICreateEntityRelationshipsResponse>("/api/entity/CreateEntityRelationships").post(query).$promise;
	}

	deleteEntityRelationships(relationshipIDs: string[]) {
		var query = {
			children: relationshipIDs
		}

		return this.toRLService<Models.ICreateEntityResponse>("/api/entity/DeleteEntityRelationships").post(query).$promise;
	}

	createEntityAndRelationships(charTypeID: number, templateID: number, title: string, relationshipCharTypeID: number, recIDs: number[]) {
		var query = {
			charTypeID: charTypeID,
			templateID: templateID,
			title: title,
			relationshipCharTypeID: relationshipCharTypeID,
			relationshipRecordIDs: recIDs
		}

		return this.toRLService<Models.ICreateEntityResponse>("/api/entity/CreateEntityAndRelationships").post(query).$promise;
	}

	getTreeListValue(charTypeID: number, templateID: number, charID: number) {
		var data = {
			charTypeID: charTypeID || -1,
			templateID: templateID,
			charID: charID || -1
		};
		return this.toRLService<Models.IFancyTreeNode>("/api/entity/getfancytreechartreelist").query(data).$promise;
	}

	getTreeListValueBySourceID(charValueSourceID: string) {
		var data = {
			charValueSourceID: charValueSourceID
		};
		return this.toRLService<Models.IFancyTreeNode>("/api/entity/getfancytreechartreelist").query(data).$promise;
	}

	getEntity(entityID: string) {
		var key = `getEntity_${entityID}`;
		return this.localCacheService.getCachedOrDoCachePromise(key, () => this.toRLService<Models.IEntityCharDataPair>("/api/entity/getentity").get({ entityID: entityID }).$promise, moment.duration(0, 'seconds'));
	}

	getEntityRecord(entityID: string) {
		var key = `getEntityRecord_${entityID}`;
		return this.localCacheService.getCachedOrDoCachePromise(key, () => this.toRLService<Models.IEntity>("/api/entity/getentityrecord").get({ entityID: entityID }).$promise, moment.duration(0, 'seconds'));
	}

	getEntityCharData(entityID: string) {
		return this.toRLService<Models.ICharDataCollection>("/api/entity/getentitychardata").get({ entityID: entityID }).$promise;
	}

	getEntityCharDataRow(entityID: string, charID: number) {
		return this.toRLService<Models.ICharDataValue>("/api/entity/getentitychardatarow").get({ entityID: entityID, charID: charID }).$promise;
	}

	getEntityCharDatas(divID: number, charTypeID: number, recIDs: number[]) {
		var data = {
			divID: divID,
			charTypeID: charTypeID,
			recIDs: recIDs
		};
		return this.toRLService<Models.ICharDataCollection>("/api/entity/getentitychardatas").query(data).$promise;
	}

	validateRecIDsOrTitlesForRelationship(parentCharTypeID: number, parentTemplateID: number, childCharTypeID: number, recIDsOrTitles: boolean, jsonRecIDsOrTitles: string, parentRecId: number = null) {
		var data = {
			parentCharTypeID: parentCharTypeID,
			parentTemplateID: parentTemplateID,
			childCharTypeID: childCharTypeID,
			recIDsOrTitles: recIDsOrTitles,
			jsonRecIDsOrTitles: jsonRecIDsOrTitles,
			parentRecId: parentRecId
		};
		return this.toRLService<Models.IDataResponse<Models.IValidateRecIDsOrTitlesResponse[]>>("/api/entity/validateRecIDsOrTitlesForRelationship").post(data).$promise;
	}

	getEmptyEntityCharData(templateID: string) {
		return this.toRLService<Models.ICharDataCollection>("/api/entity/getemptyentitychardata").get({ templateID: templateID }).$promise;
	}

	getEntityRelationships(entityID: string, charTypeID: number = -1) {
		var data = {
			entityID: entityID,
			charTypeID: charTypeID || -1
		};
		return this.toRLService<Models.IEntityRelationship>("/api/entity/getentityrelationships").query(data).$promise;
	}

	getEntityRelationship(entityID: string) {
		var data = {
			entityID: entityID
		};
		return this.toRLService<Models.IEntityRelationship>("/api/entity/getentityrelationship").get(data).$promise;
	}

	getEntityParentRelationship(entityID: string, charTypeID: number = -1) {
		var data = {
			entityID: entityID,
			charTypeID: charTypeID
		};
		return this.toRLService<Models.IEntityRelationship>("/api/entity/getentityparentrelationship").query(data).$promise;
	}

	getRelatedEntities(entityID: string, charTypeID: number = -1, loadCharData: boolean = true) {
		var data = {
			entityID: entityID,
			charTypeID: charTypeID || -1,
			loadCharData
		};
		return this.toRLService<Models.IEntityRelationshipPair>("/api/entity/getrelatedentities").query(data).$promise;
    }

    getRelatedParentEntities(entityID: string, charTypeID: number = -1) {
        var data = {
            entityID: entityID,
            charTypeID: charTypeID || -1
        };
        return this.toRLService<Models.IEntityRelationshipPair>("/api/entity/getRelatedParentEntities").query(data).$promise;
    }

	getRelatedRecords(entityID: string, charTypeID: number = -1) {
		var data = {
			entityID: entityID,
			charTypeID: charTypeID || -1
		};
		return this.toRLService<Models.IRecordRelationshipPair>("/api/entity/getrelatedrecords").query(data).$promise;
	}

	getRelatedParentRecords(entityID: string, charTypeID: number = -1) {
		var data = {
			entityID: entityID,
			charTypeID: charTypeID || -1
		};
		return this.toRLService<Models.IRecordRelationshipPair>("/api/entity/getrelatedparentrecords").query(data).$promise;
	}

	getAssociatedParentTemplatesForTemplate(charTypeID: number, templateID: number, parentCharTypeID: number) {
		var data = {
			charTypeID: charTypeID,
			templateID: templateID,
			parentCharTypeID: parentCharTypeID
		};
		var key = `getAssociatedParentTemplatesForTemplate_${charTypeID}_${templateID}_${parentCharTypeID}`;
		return this.localCacheService.getCachedOrDoCachePromise(key, () => this.toRLService<Models.ICharacteristicTemplate>("/api/entity/getassociatedparenttemplatesfortemplate").query(data).$promise, this.fiveMins);
	}

	getAssociatedTemplatesForTemplate(charTypeID: number, templateID: number, childCharTypeID: number) {
		var data = {
			charTypeID: charTypeID,
			templateID: templateID,
			childCharTypeID: childCharTypeID
		};
		var key = `getAssociatedTemplatesForTemplate_${charTypeID}_${templateID}_${childCharTypeID}`;
		return this.localCacheService.getCachedOrDoCachePromise(key, () => this.toRLService<Models.ICharacteristicTemplate>("/api/entity/getassociatedtemplatesfortemplate").query(data).$promise, this.fiveMins);
	}

	getUsedTemplates(parentEntityID: string, childCharTypeID: number) {
		var data = {
			parentEntityID,
			childCharTypeID
		}
		return this.toRLService<Models.ICharacteristicTemplate>("/api/entity/getusedtemplates").query(data).$promise;
	}

	getAssociatedTemplatesDefaults(charTypeID: number, templateID: number, childCharTypeID: number,
		relCharTypeID?: number, relChildCharTypeID?: number) {
		var data = {
			charTypeID: charTypeID,
			templateID: templateID,
			childCharTypeID: childCharTypeID,
			relCharTypeID: relCharTypeID,
			relChildCharTypeID: relChildCharTypeID
		};

		var key = `getAssociatedTemplatesDefaults_${charTypeID}_${templateID}_${childCharTypeID}_${relCharTypeID}_${relChildCharTypeID}`;
		return this.localCacheService.getCachedOrDoCachePromise(key, () => this.toRLService<Models.ITemplateDefaults>("/api/entity/GetAssociatedTemplatesDefaults").get(data).$promise, this.fiveMins);
	}

	createEntity(templateID: string, parentEntityID: string, charDatas: Models.ICharDatas, createdFromAssociation?: boolean) {
		var data = {
			templateID: templateID,
			parentEntityID: parentEntityID,
			charDatas: charDatas,
			createdFromAssociation: createdFromAssociation
		};
		return this.toRLService<{ success: boolean; entityID: string, relRecID: number }>("/api/entity/createEntity").post(data).$promise;
	}

	copyModuleLevelEntity(charTypeID: number, templateID: number, copyRelationships: boolean, parentEntityID: string, copyToTitle: string, copyParties?: boolean, notifyViaEmail?: boolean, selectAssociations?: boolean) {
		var data = {
			copyToCharTypeID: charTypeID,
			copyToTemplateID: templateID,
			copyRelationships: copyRelationships,
			entityID: parentEntityID,
			copyToTitle: copyToTitle,
			copyParties: copyParties,
			notifyViaEmail: notifyViaEmail,
			selectAssociations: selectAssociations
		};
		return this.toRLService<Models.IJobResponse>("/api/entity/CopyModuleLevelEntity").post(data).$promise;
	}

	copyComponentLevelEntity(charTypeID: number, templateID: number, copyRelationships: boolean, parentEntityID: string, copyToTitle: string, copyParties?: boolean) {
		let data: Models.ICopyEntityRequest = {
			copyToCharTypeID: charTypeID,
			copyToTemplateID: templateID,
			copyRelationships: copyRelationships,
			entityID: parentEntityID,
			copyToTitle: copyToTitle,
			copyParties: copyParties
		};
		return this.toRLService<{ success: boolean; entityID: string }>("/api/entity/copyComponentLevelEntity").post(data).$promise;
	}

	copyEntityTo(originalRelEntityID: string, parentEntityID: string, childEntityID) {
		var data = {
			originalRelEntityID: originalRelEntityID,
			parentEntityID: parentEntityID,
			entityID: childEntityID
		};
		return this.toRLService<void>("/api/entity/copyEntityTo").post(data).$promise;
	}

	public bulkCopyEntity(bulkCopyEntityRequest: Models.IBulkCopyEntityRequest) {
		return this.toRLService<Models.IJobResponse>("/api/entity/copyEntityBulk").post(bulkCopyEntityRequest).$promise;
	}

    saveUserProfileData( charDatas: Models.ICharDatas) {
        var data = {
            charDatas: charDatas
        };
        return this.toRLService<void>("/api/entity/saveuserprofiledata").post(data).$promise;
    }

	saveCharData(entityID: string, charDatas: Models.ICharDatas) {
		var data = {
			entityID: entityID,
			charDatas: charDatas
		};
		return this.toRLService<void>("/api/entity/savechardata").post(data).$promise;
	}

	saveBulkEditCharData(parentEntityID: string, charTypeID: number, templateID: number, bulkEditEntityCharDatas: Models.IBulkEntityRelationships[], editMode?: boolean, removeRelationships?: string[], addRelationships?: { [entityID: string]: string[] }, selectedCharIDs?: number[]) {
		var data = {
			parentEntityID: parentEntityID,
			charTypeID: charTypeID,
			templateID: templateID,
			bulkEntityCharDatas: bulkEditEntityCharDatas,
			editMode: editMode,
			removeRelationshipIDs: removeRelationships,
			addRelationships: addRelationships,
			selectedCharIDs: selectedCharIDs
		};

		return this.toRLService<Models.IJobResponse>("/api/entity/savebulkentitychardata").post(data).$promise;
	}

	validateEntity(entityID: string, visibility: number) {
		var data = {
			entityID: entityID,
			visibility: visibility
		};
		return this.toRLService<Models.IValidationResult>("/api/entity/validateentity").post(data).$promise;
	}

	search(request: Models.IEntitySearchRequest) {
		return this.toRLService<Models.IEntitySearchResults>("/api/entity/search").post(request).$promise;
	}

	getEntityStatus(entityID: string) {
		var data = {
			id: entityID
		};
		return this.toRLService<Models.IEntityStatus>("/api/entity/getstatus").get(data).$promise;
	}

	getEntityStatuses(charTypeID: number, recordIDs: number[]) {
		var data = {
			charTypeID: charTypeID,
			recordIDs: recordIDs.join(",")
		};
		return this.toRLService<{ [recordID: string]: Models.IEntityStatus }>("/api/entity/getstatuses").get(data).$promise;
	}

    deleteEntity(entityID: string, parentEntityID: string = null) {
		var data = {
            id: entityID,
            parentEntityID: parentEntityID
		};
        return this.toRLService<Models.IJobResponse>("/api/entity/deleteentity/:id", { id: "@id", parentEntityID: "@parentEntityID" }).post(data).$promise;
	}

	getEntityDocuments(entityID: string) {
		var data = {
			id: entityID
		};
		return this.toRLService<Models.IEntityDocumentsCollection>("/api/entity/getentitydocuments").get(data).$promise;
	}

	associateEntity(entityID: string, childEntityIDs: string[], direction?: number) {
		var data = {
			entityID: entityID,
			childEntityIDs: childEntityIDs,
			direction: direction
		};
		return this.toRLService<Models.IJobResponse>("/api/Entity/AddEntityAssociations").post(data).$promise;
	}

	removeAssociatedEntities(entityID: string, childEntityIDs: string[], direction?: number) {
		var data = {
			entityID: entityID,
			childEntityIDs: childEntityIDs,
			direction: direction
		};
		return this.toRLService<Models.IJobResponse>("/api/Entity/RemoveEntityAssociations").post(data).$promise;
	}

	getHierarchy(entityID: string, templateGroupIDs: string, includeParents: boolean) {
		var data = <Models.IHierarchyRequest>{
			entityID: entityID,
			templateGroupIDs: templateGroupIDs,
			includeParents: includeParents
		};
		return this.toRLService<Models.IEntityNodeWRelID>("/api/entity/getentityhierarchy").query(data).$promise;
	}

	getHierarchyParentsList(entityID: string, templateGroupIDs: string) {
		var data = <Models.IHierarchyRequest>{
			entityID: entityID,
			templateGroupIDs: templateGroupIDs
		};
		return this.toRLService<Models.IEntityNodeWRelID>("/api/entity/getentityhierarchyparentslist").query(data).$promise;
	}

	deleteAssociations(entityID: string, relRecIDs: number[]) {
		var data = {
			parentEntityID: entityID,
			relRecIDs: relRecIDs
		};
		return this.toRLService<boolean>("/api/entity/deleteassociations").post(data).$promise;
	}

	deleteAssociation(entityID: string, childEntityID: string) {
		var data = {
			parentEntityID: entityID,
			childEntityID: childEntityID
		};
		return this.toRLService<boolean>("/api/entity/deleteassociation").post(data).$promise;
	}

	updateAssociation(relationship: Models.IEntityRelationship) {
		var data = {
			relationship: relationship
		};
		return this.toRLService<Models.IDataResponse<boolean>>("/api/entity/updateassociation").post(data).$promise;
	}

	getEntityCreateURL(charTypeID: number, parentEntityID: string, isAssociationCreate: boolean, direction?: number) {
		var data = {
			charTypeID: charTypeID,
			parentEntityID: parentEntityID,
			isAssociationCreate: isAssociationCreate,
			direction: direction
		}
		return this.toRLService<Models.IDataResponse<string>>("/api/entity/getentitycreateurl").get(data).$promise;
	}

	updateEntityTemplate(isMigrate: boolean, entityID: string, charTypeID: number, newTitle: string, templateID: number, copyParties: boolean, setAssociations: boolean) {
		var data = {
			isMigrate: isMigrate,
			entityID: entityID,
			charTypeID: charTypeID,
			newTitle: newTitle,
			newCharTypeID: charTypeID,
			templateID: templateID,
			copyParties: copyParties,
			setAssociations: setAssociations
		}
		return this.toRLService<Models.IDataResponse<string>>("/api/entity/updateentitytemplate").post(data).$promise;
	}

	updateEntityTemplateEntityCheck(isMigrate: boolean, entityID: string, charTypeID: number, newTitle: string, newCharTypeID: number, newTemplateID: number, copyParties: boolean, setAssociations: boolean) {
		var data = {
			isMigrate: isMigrate,
			entityID: entityID,
			charTypeID: charTypeID,
			newTitle: newTitle,
			newCharTypeID: newCharTypeID,
			templateID: newTemplateID,
			copyParties: copyParties,
			setAssociations: setAssociations
		}
		return this.toRLService<Models.IDataResponse<Models.IUpdateEntityTemplateEntityCheck>>("/api/entity/updateentitytemplateentitycheck").post(data).$promise;
	}

	updateEntityTemplateRelationshipsCheck(isMigrate: boolean, entityID: string, charTypeID: number, newTitle: string, newCharTypeID: number, newTemplateID: number, copyParties: boolean, setAssociations: boolean) {
		var data = {
			isMigrate: isMigrate,
			entityID: entityID,
			charTypeID: charTypeID,
			newTitle: newTitle,
			newCharTypeID: newCharTypeID,
			templateID: newTemplateID,
			copyParties: copyParties,
			setAssociations: setAssociations
		}
		return this.toRLService<Models.IDataResponse<Models.IUpdateEntityTemplateRelationshipsCheck>>("/api/entity/updateentitytemplaterelationshipscheck").post(data).$promise;
	}

	updateEntityAssociations(entityID: string, associations: Models.ICharTypeTemplateAssociationSelection[], originalEntityID: string, createIntraCharRelationship: boolean, intraCharRelationshipDirection: number) {
		var data = {
			entityID: entityID,
			associations: associations,
			originalEntityID: originalEntityID,
			createRelationship: createIntraCharRelationship,
			relationshipDirection: intraCharRelationshipDirection
		}
		return this.toRLService<Models.IJobResponse>("/api/entity/updateentityassociations").post(data).$promise;
	}

	getEntityMigrateURL(charTypeID: number, newEntity: string, templateID: number) {
		var data = {
			charTypeID: charTypeID,
			newEntity: newEntity,
			templateID: templateID
		}
		return this.toRLService<Models.IDataResponse<string>>("/api/entity/getentitymigrateurl").get(data).$promise;
	}

	createDataDefaultSet(charTypeID: number, setCharTypeID: number, recordIDs: number[], label: string,
		relCharTypeID?: number, relChildCharTypeID?: number) {
		var data = {
			charTypeID: charTypeID,
			setCharTypeID: setCharTypeID,
			recordIDs: recordIDs,
			label: label,
			relCharTypeID: relCharTypeID,
			relChildCharTypeID: relChildCharTypeID
		};
		var promise = this.toRLService<{ id: string }>("/api/entity/CreateDataDefaultSet").post(data).$promise;
		promise.then(() => this.localCacheService.clearAll());
		return promise;
	}

	copyDataDefaultSet(setID: string, charTypeID: number, recordID: number, copyTitles: boolean = false) {
		var data = {
			setID: setID,
			charTypeID: charTypeID,
			recordID: recordID,
			copyTitles: copyTitles
		}

		var promise = this.toRLService<Models.IDataResponse<Models.IEntityID[]>>("/api/entity/CopyDataDefaultSet").post(data).$promise;
		promise.then(() => this.localCacheService.clearAll());
		return promise;
	}

	deleteDataDefaultSet(setID: string) {
		var promise = this.toRLService<{ id: string }>("/api/entity/DeleteDataDefaultSet").delete({ id: setID }).$promise;
		promise.then(() => this.localCacheService.clearAll());
		return promise;
	}

	validateChildTemplateForParentEntity(parentEntityID: string, childEntityID: string) {
		var data = {
			parentEntityID: parentEntityID,
			childEntityID: childEntityID
		};
		return this.toRLService<Models.IDataResponse<boolean>>("/api/entity/validatechildtemplateforparententity").get(data).$promise;
	}

	exportAssociationHierarchyToCSV(entityID: string, solrColumnNames: Models.IKeyValuePair<string, number>[]) {
		var data = {
			entityID: entityID,
			solrColumnNames: solrColumnNames
		};
		return this.toRLService<Models.IJobResponse>("/api/entity/exportassociationhierarchytocsv").post(data).$promise;
	}

	getRecordDocumentsForRecordIDs(request: Models.ISearchResultByRecordIDsRequest) {
		return this.toRLService<Models.AdvancedSearchResult>("/api/Search/GetSolrSearchResultForRecIDs").post(request).$promise;
	}

	searchTypeAhead(charTypeID: number, templateID: number, keywords: string, recordID: number, parentCharTypeID: number, parentRecID: number) {
		var data = {
			charTypeID: charTypeID,
			templateID: templateID,
			keywords: keywords,
			recordID: recordID,
			parentCharTypeID: parentCharTypeID,
			parentRecID: parentRecID
		};
		return this.toRLService<Models.IEntitySearchResults>("/api/entity/searchtypeahead/").get(data).$promise;
	}

	searchForUniqueTitle(charTypeID: number, templateID: number, searchField: string, dataTypeID: number, title: string, recordID: number, parentCharTypeID: number, parentRecID: number, characteristicID: number) {
		var data = {
			charTypeID: charTypeID,
			templateID: templateID,
			searchField: searchField,
			dataTypeID: dataTypeID,
			title: title,
			recordID: recordID,
			parentCharTypeID: parentCharTypeID,
			parentRecID: parentRecID,
			characteristicID: characteristicID
		};
		return this.toRLService<Models.IDataResponse<boolean>>("/api/entity/SearchForUniqueTitle/").get(data).$promise;
	}

	createHierachicalRecords(entityID: string, templateHierarchyRecords: Models.ITemplateHierarchy[]) {
		var data = {
			entityID: entityID,
			templateHierarchicalLevels: templateHierarchyRecords
		};
		return this.toRLService<Models.IJobResponse>("/api/entity/CreateAndAssociateHierarchicalRecords/").post(data).$promise;
	}

	searchCatalogRights(catalogID: number, startDate: string, endDate: string, titleKeywords: string,
		filters: any, facetFields: string[], page: number, rows: number, sortDir: number, sortField: string,
		groupBy: string, groupID: number, selectedPaths: { [label: string]: string[] }) {
		var data = {
			parentRecordID: catalogID,
			startDate: startDate,
			endDate: endDate,
			titleKeywords: titleKeywords,
			page: page,
			rows: rows,
			filters: filters,
			facetFields: facetFields,
			sortDir: sortDir,
			sortField: sortField,
			groupBy: groupBy,
			groupID: groupID,
			selectedPaths: selectedPaths
		};
		return this.toRLService<Models.ISearchCatalogRightsResponse>("/api/asset/searchcatalogitemrights").post(data).$promise;
	}

	refreshRelsForCatalogItems(entityID: string, catalogEntityID: string) {
		var data = {
			entityID: entityID,
			catalogItemID: catalogEntityID
		};
		return this.toRLService<void>("/api/entity/refreshParentRelsForCatalogItems").post(data).$promise;
	}

	syncDealCatalogRightsRels(entityID: string) {
		var data = {
			entityID: entityID
		};
		return this.toRLService<void>("/api/entity/syncDealCatalogRightsRels").post(data).$promise;
	}

	getChildNotExistingParentEntityRelationshipList(parentEntityID: string, childCharTypeID: number, childRecIDs: number[]) {
		var data = {
			parentEntityID: parentEntityID,
			childCharTypeID: childCharTypeID,
			childRecIDs: childRecIDs
		};
		return this.toRLService<Models.IDataResponse<Models.IChildNotExistingParentEntityRelationshipList[]>>("/api/entity/getChildNotExistingParentEntityRelationshipList").post(data).$promise;
	}

	bulkUpdateRelationships(createRelationships: Models.IEntityRelationship[], deleteRelationships: string[]) {
		let data = {
			createRelationships: createRelationships,
			deleteRelationships: deleteRelationships
		}
		return this.toRLService<Models.IDataResponse<Models.IChildNotExistingParentEntityRelationshipList[]>>("/api/entity/BulkUpdateRelationships").post(data).$promise;
	}

	bulkDeleteEntities(entityIDs: string[]) {
		var data = {
			entityIDs
		};

		return this.toRLService<Models.IJobResponse>("/api/entity/bulkdeleteentities/").post(data).$promise;
	}

	syncMetaDataOrPartiesToHierarchicalRecords(request: Models.ISyncMetaDataOrPartiesToHierarchicalRecordsRequest) {
		return this.toRLService<Models.IJobResponse>("/api/entity/syncMetaDataOrPartiesToHierarchicalRecords").post(request).$promise;
	}

	exportDataToCSV(parentEntityID: string,
		exportDataTemplateIDs: number[],
		exportDataCharTypeID: number,
		sortField: string,
		sortDirection: number,
		titleKeywords?: string,
		startDate?: string,
		endDate?: string,
		filters?: { [solrLabel: string]: string | string[] },
		selectedPaths?: { [label: string]: string[] },
		solrColumns?: Models.IKeyValuePair<string, number>[]) {
		const data = {
			parentEntityID,
			exportDataTemplateIDs,
			exportDataCharTypeID,
			sortField,
			sortDirection,
			titleKeywords,
			startDate,
			endDate,
			filters,
			selectedPaths,
			solrColumns
		};
		return this.toRLService<Models.IJobResponse>("/api/entity/exportdatatocsv").post(data).$promise;
	}

	createEntityWithOptions(templateID: string, parentEntityID: string, charDatas: Models.ICharDatas, childCharTypeIDs: number[], childEntityIDs: string[]) {
		var data = {
			templateID: templateID,
			parentEntityID: parentEntityID,
			charDatas: charDatas,
			childCharTypeIDs: childCharTypeIDs,
			childEntityIDs: childEntityIDs
		};
		return this.toRLService<{ success: boolean; entityID: string }>("/api/entity/createentitywithoptions").post(data).$promise;
	}

	getEntityDocumentsHierarchyAsFlatList(entityID: string) {
		var data = {
			id: entityID
		};
		return this.toRLService<{ [recordID: number]: string }>("/api/entity/getEntityDocumentsHierarchyAsFlatList").get(data).$promise;
	}

	getEntityDocumentDownloadUrls(entityID: string) {
		var data = {
			id: entityID
		};
		return this.toRLService<{ [recordID: number]: string }>("/api/entity/getEntityDocumentDownloadUrls").get(data).$promise;
	}

	updateComponentSequence(charTypeID: number, recordID: number, templateID: number, templateGroupID: number, childCharTypeID: number, sortByCharTypeID: number) {
		var data = {
			charTypeID: charTypeID,
			recordID: recordID,
			templateID: templateID,
			templateGroupID: templateGroupID,
			childCharTypeID: childCharTypeID,
			sortByCharTypeID: sortByCharTypeID
		};

		return this.toRLService<Models.IDataResponse<number>>("/api/entity/updateComponentSequence").get(data).$promise;
	}

	applyRelativeRightsProfile(rrpEntityRecID: number, rrpAdditionalOption: number, recIDs?: number[]) {
		var data = {
			entityRecIDs: recIDs,
			rrpRecID: rrpEntityRecID,
			option: rrpAdditionalOption
		};

		return this.toRLService<Models.IJobResponse>("/api/entity/applyrelativerightsprofile").post(data).$promise;
	}

	applyRelativeRightsProfileFromDeal(rrpEntityRecID: number, rrpAdditionalOption: number, dealRecID: number, recIDs?: number[]) {
		var data = {
			entityRecIDs: recIDs,
			rrpRecID: rrpEntityRecID,
			option: rrpAdditionalOption,
			dealRecID: dealRecID
		};
		return this.toRLService<Models.IJobResponse>("/api/entity/applyrelativerightsprofile").post(data).$promise;
	}

	setRecordOrder(entityID: string, ChildCharTypeID: number, recIDAndSequence: { [recID: number]: number }) {
		var data = {
			entityID: entityID,
			recIDAndSequence: recIDAndSequence,
			ChildCharTypeID: ChildCharTypeID
		};

		return this.toRLService<void>("/api/entity/setrecordorder").post(data).$promise;
	}

	getExistingCatalogItemsFromDealToInvoices(recordId: number, childRecordIds: number[]) {
		var data = {
			RecordID: recordId,
			CatalogItemRecIDs: childRecordIds
		};

		return this.toRLService<Models.IDataResponse<number[]>>("/api/entity/getExistingCatalogItemsFromDealToInvoices").post(data).$promise;
	}
}
